import React, { useMemo } from 'react';
import { TrashBin } from '@gravity-ui/icons';
import { TimeInput } from '../../../components/DateTimeInput';
import {
  ACTIVATION_ALWAYS,
  ACTIVATION_SCHEDULED,
  createWindow,
  validateActivation,
} from '../../../utils/violationSchedule';
import { DEFAULT_TIMEZONE, WEEKDAYS, listTimezones } from '../../../constants/violationConstants';

/*
 * P2-01 / Активация правила (ТЗ Part 2, разделы 3 и 4).
 *
 * Контролируемый компонент: модель { mode, timezone, windows } хранит родитель.
 * Cross-midnight — одно окно, фронт его не разбивает на два.
 */

const ActivationEditor = ({ value, onChange, disabled = false }) => {
  const timezones = useMemo(() => listTimezones(), []);
  const validation = validateActivation(value);
  const scheduled = value.mode === ACTIVATION_SCHEDULED;

  const setMode = (mode) => {
    if (mode === ACTIVATION_ALWAYS) {
      onChange({ ...value, mode: ACTIVATION_ALWAYS });
      return;
    }
    onChange({
      mode: ACTIVATION_SCHEDULED,
      timezone: value.timezone || DEFAULT_TIMEZONE,
      windows: value.windows.length ? value.windows : [createWindow()],
    });
  };

  const patchWindow = (id, patch) => onChange({
    ...value,
    windows: value.windows.map((window) => (window.id === id ? { ...window, ...patch } : window)),
  });

  const removeWindow = (id) => onChange({
    ...value,
    windows: value.windows.filter((window) => window.id !== id),
  });

  const addWindow = () => onChange({ ...value, windows: [...value.windows, createWindow()] });

  return (
    <div className="vio-activation">
      <p className="vio-drawer-section-title">Активация правила</p>

      <div className="vio-activation-modes">
        <label className="vio-activation-mode">
          <input
            type="radio"
            name="vio-activation-mode"
            aria-label="По расписанию"
            checked={scheduled}
            disabled={disabled}
            onChange={() => setMode(ACTIVATION_SCHEDULED)}
          />
          По расписанию
        </label>
        <label className="vio-activation-mode">
          <input
            type="radio"
            name="vio-activation-mode"
            aria-label="Всегда активно"
            checked={!scheduled}
            disabled={disabled}
            onChange={() => setMode(ACTIVATION_ALWAYS)}
          />
          Всегда активно
        </label>
      </div>

      {scheduled && (
        <>
          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-activation-timezone">Часовой пояс</label>
            <select
              id="vio-activation-timezone"
              aria-label="Часовой пояс"
              value={value.timezone}
              disabled={disabled}
              onChange={(event) => onChange({ ...value, timezone: event.target.value })}
            >
              <option value="">— выберите —</option>
              {timezones.map((zone) => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
            {validation.timezoneError && (
              <p className="vio-field-error">{validation.timezoneError}</p>
            )}
          </div>

          <div className="vio-activation-windows">
            {value.windows.map((window) => (
              <div className="vio-activation-window" key={window.id}>
                <div className="vio-activation-row">
                  <select
                    aria-label="День недели"
                    value={window.weekday}
                    disabled={disabled}
                    onChange={(event) => patchWindow(window.id, { weekday: Number(event.target.value) })}
                  >
                    {WEEKDAYS.map((day) => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                  <TimeInput
                    ariaLabel="Начало"
                    value={window.startTime}
                    disabled={disabled}
                    onChange={(next) => patchWindow(window.id, { startTime: next })}
                  />
                  <TimeInput
                    ariaLabel="Окончание"
                    value={window.endTime}
                    disabled={disabled}
                    onChange={(next) => patchWindow(window.id, { endTime: next })}
                  />
                  <button
                    type="button"
                    className="vio-btn vio-btn--icon"
                    aria-label="Удалить интервал"
                    disabled={disabled}
                    onClick={() => removeWindow(window.id)}
                  >
                    <TrashBin width={16} height={16} />
                  </button>
                </div>
                {validation.windowErrors[window.id] && (
                  <p className="vio-field-error">{validation.windowErrors[window.id]}</p>
                )}
              </div>
            ))}
          </div>

          {validation.generalError && <p className="vio-field-error">{validation.generalError}</p>}

          <button type="button" className="vio-btn vio-btn--link" disabled={disabled} onClick={addWindow}>
            + Добавить интервал
          </button>
        </>
      )}
    </div>
  );
};

export default ActivationEditor;
